import React, { useState, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Gift, Copy, Check, Users, TrendingUp, Link as LinkIcon, Twitter, Send,
  MessageCircle, Share2, Sparkles, Trophy, Rocket, Wallet, Percent, Clock,
  Download,
} from 'lucide-react';
import { getHost, getOrigin } from '../lib/site';

const REF_CODE = 'pfs_1337x';

function ShareCard({ host }) {
  const ref = useRef(null);
  const [rendering, setRendering] = useState(false);

  const draw = (forDownload = false) => {
    const canvas = ref.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const w = 1200, h = 630;
    canvas.width = w; canvas.height = h;

    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#020604');
    g.addColorStop(0.55, '#031508');
    g.addColorStop(1, '#000a04');
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);

    const orb = ctx.createRadialGradient(w - 180, 120, 20, w - 180, 120, 400);
    orb.addColorStop(0, 'rgba(0,255,102,0.45)');
    orb.addColorStop(1, 'rgba(0,255,102,0)');
    ctx.fillStyle = orb; ctx.fillRect(0, 0, w, h);
    const orb2 = ctx.createRadialGradient(120, h - 80, 10, 120, h - 80, 360);
    orb2.addColorStop(0, 'rgba(0,255,102,0.25)');
    orb2.addColorStop(1, 'rgba(0,255,102,0)');
    ctx.fillStyle = orb2; ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = 'rgba(0,255,102,0.08)';
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 44) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
    for (let y = 0; y < h; y += 44) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

    ctx.fillStyle = 'rgba(0,255,102,0.12)';
    ctx.strokeStyle = 'rgba(0,255,102,0.5)';
    ctx.lineWidth = 1;
    const chipW = 270, chipH = 40, chipX = 80, chipY = 80;
    roundRect(ctx, chipX, chipY, chipW, chipH, 20);
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#00ff66';
    ctx.font = 'bold 16px "JetBrains Mono", monospace';
    ctx.fillText('✦  REFERRAL · EARN 30% FOR LIFE', chipX + 20, chipY + 26);

    ctx.fillStyle = '#ffffff';
    ctx.font = '900 84px "Unbounded", system-ui, sans-serif';
    ctx.fillText('Snipe smarter.', 80, 240);
    ctx.fillStyle = '#00ff66';
    ctx.fillText('Earn together.', 80, 330);

    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.font = '500 28px "Inter", sans-serif';
    ctx.fillText('Use my link — we both get +0.5 SOL signup bonus.', 80, 384);

    ctx.fillStyle = '#00ff66';
    roundRect(ctx, 80, 430, 340, 76, 14); ctx.fill();
    ctx.fillStyle = '#001309';
    ctx.font = '800 28px "Unbounded", sans-serif';
    ctx.fillText('Launch Terminal  →', 112, 478);

    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '600 22px "JetBrains Mono", monospace';
    ctx.fillText(`${host}/?ref=${REF_CODE}`, 80, 560);

    ctx.fillStyle = 'rgba(0,255,102,0.1)';
    ctx.strokeStyle = 'rgba(0,255,102,0.4)';
    roundRect(ctx, w - 360, 120, 260, 260, 28);
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#00ff66';
    ctx.font = '900 110px "Unbounded", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('PFS', w - 230, 275);
    ctx.font = '700 22px "JetBrains Mono", monospace';
    ctx.fillText('PumpFun Sniper', w - 230, 340);
    ctx.textAlign = 'start';

    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    roundRect(ctx, w - 420, h - 110, 340, 70, 14);
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#00ff66';
    ctx.font = '900 34px "Unbounded", sans-serif';
    ctx.fillText('4.7 / 5', w - 400, h - 62);
    ctx.fillStyle = 'rgba(255,255,255,0.65)';
    ctx.font = '500 14px "Inter", sans-serif';
    ctx.fillText('Trustpilot · 2,817 reviews', w - 400, h - 45);
    ctx.fillText('★ ★ ★ ★ ★', w - 260, h - 76);

    if (forDownload) {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `pumpfun-sniper-referral-${REF_CODE}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }, 'image/png');
    }
  };

  const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  };

  React.useEffect(() => { draw(false); }, []);

  return (
    <div className="glass-card overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border/50 text-xs font-mono uppercase tracking-wider text-muted-foreground">
        <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
        <span>Auto-generated share card (1200×630)</span>
        <button
          data-testid="download-share-card"
          onClick={() => { setRendering(true); draw(true); setTimeout(() => setRendering(false), 500); }}
          className="ml-auto btn-ghost !px-3 !py-1.5 text-xs"
        >
          <Download className="w-3.5 h-3.5" /> {rendering ? 'Exporting…' : 'Download PNG'}
        </button>
      </div>
      <div className="relative bg-black">
        <canvas ref={ref} className="w-full block" />
      </div>
    </div>
  );
}

export default function Referral() {
  const host = getHost();
  const REF_LINK = useMemo(() => `${getOrigin()}/?ref=${REF_CODE}`, []);
  const TWEET_TEXT = `I've been using PumpFun Sniper to snipe pump.fun launches — the AI rugpull shield is absurdly good 🎯

Join with my link and we both get +0.5 SOL:
${REF_LINK}

Community: https://t.me/Bot_PumpFun`;
  const TELEGRAM_TEXT = `🚀 PumpFun Sniper — fastest Solana sniper bot I've tried.\n\n✅ Audited by CertiK + OpenZeppelin\n✅ $100K bug bounty\n✅ Sub-10ms execution\n\nUse my link, we both get +0.5 SOL:\n${REF_LINK}\n\nCommunity: https://t.me/Bot_PumpFun`;

  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const copy = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(TWEET_TEXT)}`;
  const telegramHref = `https://t.me/share/url?url=${encodeURIComponent(REF_LINK)}&text=${encodeURIComponent(TELEGRAM_TEXT)}`;
  const whatsappHref = `https://api.whatsapp.com/send?text=${encodeURIComponent(TELEGRAM_TEXT)}`;

  return (
    <div className="pt-12 sm:pt-16 pb-16 sm:pb-20 container-x" data-testid="referral-page">
      <section className="text-center py-10">
        <span className="chip"><Gift className="w-3 h-3" /> Referral program</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          Earn <span className="gradient-text">30%</span> for life
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
          Invite a friend. They get a <span className="text-[hsl(var(--primary))] font-semibold">+0.5 SOL</span> signup bonus,
          you get <span className="text-[hsl(var(--primary))] font-semibold">30%</span> of their plan revenue forever, paid weekly.
        </p>
      </section>

      <div className="grid md:grid-cols-4 gap-3 mt-2">
        {[
          { Icon: Wallet,     v: '0.5 SOL',   k: 'Signup bonus' },
          { Icon: Percent,    v: '30%',       k: 'Lifetime revenue share' },
          { Icon: Clock,      v: 'Weekly',    k: 'Payout cadence' },
          { Icon: Trophy,     v: 'Top 50%',   k: 'Scale to higher tier' },
        ].map((r) => (
          <motion.div key={r.k}
            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="glass-card p-5">
            <r.Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
            <div className="font-display font-black text-2xl tracking-tight mt-2">{r.v}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{r.k}</div>
          </motion.div>
        ))}
      </div>

      {}
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-5 mt-8">
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-3 text-sm font-mono uppercase tracking-wider text-muted-foreground">
            <LinkIcon className="w-4 h-4" /> Your unique link
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input readOnly value={REF_LINK}
              data-testid="referral-link-input"
              className="flex-1 px-4 py-3 rounded-lg bg-background/60 border border-border font-mono text-sm focus:outline-none focus:border-[hsl(var(--primary))]" />
            <button onClick={() => copy(REF_LINK, setCopied)} className="btn-primary" data-testid="copy-referral-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
            </button>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background/50 border border-border font-mono text-xs">
              <span className="text-muted-foreground">code</span>
              <span className="text-[hsl(var(--primary))] font-semibold">{REF_CODE}</span>
              <button onClick={() => copy(REF_CODE, setCopiedCode)}
                className="text-muted-foreground hover:text-[hsl(var(--primary))]">
                {copiedCode ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
            <span className="text-xs text-muted-foreground">Share the link or just the code.</span>
          </div>

          <div className="mt-6">
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <Share2 className="w-3.5 h-3.5" /> Share with one click
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <a
                href={twitterHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-twitter-btn"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-border hover:border-sky-400/60 hover:bg-sky-400/5 transition-all"
              >
                <span className="w-9 h-9 rounded-lg bg-sky-500/10 text-sky-400 border border-sky-400/30 flex items-center justify-center">
                  <Twitter className="w-4 h-4" />
                </span>
                <div className="text-left">
                  <div className="text-sm font-semibold">Share on Twitter</div>
                  <div className="text-[11px] text-muted-foreground">Auto-fills tweet with link & emoji</div>
                </div>
                <Rocket className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-sky-400" />
              </a>
              <a
                href={telegramHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-telegram-btn"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-border hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-all"
              >
                <span className="w-9 h-9 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 flex items-center justify-center">
                  <Send className="w-4 h-4" />
                </span>
                <div className="text-left">
                  <div className="text-sm font-semibold">Share on Telegram</div>
                  <div className="text-[11px] text-muted-foreground">Opens share sheet with rich text</div>
                </div>
                <Rocket className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-[hsl(var(--primary))]" />
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-whatsapp-btn"
                className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-border hover:border-emerald-400/60 hover:bg-emerald-400/5 transition-all"
              >
                <span className="w-9 h-9 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-400/30 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </span>
                <div className="text-left">
                  <div className="text-sm font-semibold">Share on WhatsApp</div>
                  <div className="text-[11px] text-muted-foreground">Great for 1-on-1 invites</div>
                </div>
              </a>
            </div>
          </div>

          <details className="mt-5">
            <summary className="text-xs font-mono uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground">
              Preview share text →
            </summary>
            <div className="grid gap-3 mt-3">
              <div className="p-4 rounded-lg bg-background/50 border border-border">
                <div className="text-[10px] font-mono uppercase tracking-wider text-sky-400 mb-1">Twitter</div>
                <p className="text-sm whitespace-pre-wrap text-foreground/90">{TWEET_TEXT}</p>
              </div>
              <div className="p-4 rounded-lg bg-background/50 border border-border">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--primary))] mb-1">Telegram / WhatsApp</div>
                <p className="text-sm whitespace-pre-wrap text-foreground/90">{TELEGRAM_TEXT}</p>
              </div>
            </div>
          </details>
        </div>

        <div>
          <ShareCard host={host} />
          <p className="text-xs text-muted-foreground mt-3">
            Tip: download the PNG and attach it in any DM or channel — big platforms (Twitter, Telegram, WhatsApp) auto-embed it.
          </p>
        </div>
      </div>

      {}
      <h2 className="font-display font-black text-3xl mt-14 mb-5 tracking-tight">Top earners this month</h2>
      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-black/30 border-b border-border/60 font-mono text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3 text-left">Rank</th>
              <th className="px-5 py-3 text-left">Trader</th>
              <th className="px-5 py-3 text-right">Referrals</th>
              <th className="px-5 py-3 text-right">Lifetime SOL</th>
            </tr>
          </thead>
          <tbody>
            {[
              { n: '0xWhale',     r: 482, s: '1,284.3' },
              { n: 'degen_queen', r: 312, s: '724.1' },
              { n: 'SOLninja',    r: 268, s: '512.8' },
              { n: 'Alpha_King',  r: 184, s: '398.2' },
              { n: 'moon_chad',   r: 132, s: '244.7' },
            ].map((r, i) => (
              <tr key={r.n} className="border-b border-border/40 last:border-0">
                <td className="px-5 py-3 font-mono">#{i + 1}</td>
                <td className="px-5 py-3 font-semibold flex items-center gap-2"><Users className="w-3.5 h-3.5 text-muted-foreground" />{r.n}</td>
                <td className="px-5 py-3 text-right font-mono">{r.r}</td>
                <td className="px-5 py-3 text-right font-mono text-[hsl(var(--primary))] font-bold">{r.s}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="glass-card p-6 mt-8 text-center">
        <TrendingUp className="w-8 h-8 text-[hsl(var(--primary))] mx-auto" />
        <h3 className="font-display font-bold text-lg mt-2">Scale with us</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl mx-auto">
          Top 10 affiliates unlock custom contracts, higher tiers up to 50%, and co-marketing budget.
        </p>
      </div>
    </div>
  );
}
